import dynamic from "next/dynamic";

// Maps exhibit module __typenames to components (modeled on FlexibleContentMap).
// Unmapped layouts render nothing — add new modules here as they are built.
//
// DEEP-LINKING + SHARING CHECKLIST for a new shareable/linkable module (the
// not-yet-built quiz, music explorer, and deep dive are the known cases — see
// docs/digital-exhibit-deep-linking.md):
//   1. Give the module's root <section> a stable id `dx-<slug>` (slug = the
//      share slug, e.g. `quiz`). That alone makes it deep-linkable from CTAs
//      anywhere on the site via a `#<slug>` hash — single-digital-exhibit.tsx
//      resolves it; no extra wiring needed. Cards = `dx-card-gallery`,
//      Carousel = `dx-carousel` today.
//   2. Add `<slug> -> <__typename>` to MODULE_TYPENAMES in
//      src/pages/api/dx-og/index.ts so `?module=<slug>` shares render OG meta.
//   3. Clone the Digital Exhibit Share component (shareEnabled/OG) into the
//      module's ACF group, like Card Gallery / Carousel.
//   4. Document the slug as a valid section ID for editors entering CTA links.
const componentMap: Record<string, React.ComponentType<any>> = {
  DigitalExhibitContentModulesBigTextLayout: dynamic(
    () => import("./DigitalExhibitBigText/DigitalExhibitBigText")
  ),
  DigitalExhibitContentModulesCardGalleryLayout: dynamic(
    () => import("./DigitalExhibitCardGallery/DigitalExhibitCardGallery")
  ),
  DigitalExhibitContentModulesCarouselHighlightLayout: dynamic(
    () => import("./DigitalExhibitCarousel/DigitalExhibitCarousel")
  ),
  DigitalExhibitContentModulesDeepDiveLayout: dynamic(
    () => import("./DigitalExhibitDeepDive/DigitalExhibitDeepDive")
  ),
  DigitalExhibitContentModulesMusicExplorerLayout: dynamic(
    () => import("./DigitalExhibitMusicExplorer/DigitalExhibitMusicExplorer")
  ),
  DigitalExhibitContentModulesQuizLayout: dynamic(
    () => import("./DigitalExhibitQuiz/DigitalExhibitQuiz")
  ),
};

type ContentMapProps = {
  modules?: ({ __typename?: string } | null)[] | null;
  fontTheme?: string;
  textCase?: string;
};

export default function DigitalExhibitContentMap({ modules, fontTheme, textCase }: ContentMapProps) {
  if (!modules?.length) return null;
  return (
    <>
      {modules.map((module, index) => {
        if (!module?.__typename) return null;
        const Component = componentMap[module.__typename];
        if (!Component) return null;
        return <Component key={index} {...module} fontTheme={fontTheme} textCase={textCase} />;
      })}
    </>
  );
}
